// =============================================================================
// SimSense Platform — Multi-Lap Telemetry Graph
// =============================================================================

import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

type TelemetryHoverDetail = {
  idx: number | null;
  lapDistPct: number | null;
};

interface MultiLapGraphProps {
  lapsData: any[]; // Array of telemetry objects { Speed: [], Throttle: [], ... }
  lapLabels: string[]; // ["Lap 5", "Lap 12"]
  channel: string; // "Speed", "Throttle", "Brake"
  height?: number;
  onHover?: (detail: TelemetryHoverDetail) => void;
  units?: "metric" | "standard";
  trackModel?: any;
  colors?: string[];
  embedded?: boolean; // When true, skip outer card styling (used when nested in a parent card)
  zoomRange?: [number, number] | null; // OT-315: Shared zoom range [min, max] on x-axis
}

export function MultiLapGraph({ lapsData, lapLabels, channel, height = 300, onHover, units = "standard", trackModel, colors: customColors, embedded, zoomRange }: MultiLapGraphProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const uPlotRef = useRef<uPlot | null>(null);
  const suppressZoomRef = useRef(false); // OT-315: Prevent re-entrant zoom dispatch
  const suppressHoverRef = useRef(false); // Prevent cursor feedback loops on map-originated jumps

  // Channels that need unit conversion from iRacing native units
  const isMetric = units === "metric";
  const CHANNEL_CONVERSIONS: Record<string, { factor: number; unit: string; label: string }> = {
    Speed: { factor: isMetric ? 3.6 : 2.23694, unit: isMetric ? " km/h" : " mph", label: "Speed" },
    SteeringWheelAngle: { factor: 57.2958, unit: "°", label: "Steering Angle" },

    Throttle: { factor: 1, unit: "%", label: "Throttle" },
    Brake: { factor: 1, unit: "%", label: "Brake" },
  };

  const conversion = CHANNEL_CONVERSIONS[channel];

  useEffect(() => {
    if (!chartRef.current || lapsData.length === 0) return;

    // Destroy existing chart
    if (uPlotRef.current) {
      uPlotRef.current.destroy();
    }

    // 1. Prepare Data for uPlot
    // OT-111: lapsData is pre-interpolated to 1000 points.
    const x = lapsData[0].LapDistPct || [];
    const data: any[] = [x];

    lapsData.forEach(lap => {
      const raw = lap[channel] || [];
      data.push(conversion ? raw.map((v: number) => v * conversion.factor) : raw);
    });

    // 2. Configure Options
    const opts: uPlot.Options = {
      title: "",
      id: `chart-${channel.toLowerCase()}`,
      class: "osc-chart",
      width: chartRef.current.clientWidth,
      height: height,
      hooks: {
        setCursor: [
          (u) => {
            if (suppressHoverRef.current) return;
            const idx = u.cursor.idx;
            if (!onHover) return;
            if (idx == null || idx < 0) {
              onHover({ idx: null, lapDistPct: null });
              return;
            }
            const lapDistPct = typeof u.data?.[0]?.[idx] === "number" ? Number(u.data[0][idx]) : null;
            onHover({ idx, lapDistPct });
          }
        ],
        setScale: [
          (u, key) => {
            // OT-315: When user drag-zooms on x-axis, broadcast zoom range to all graphs
            if (key === "x" && !suppressZoomRef.current) {
              const min = u.scales.x.min;
              const max = u.scales.x.max;
              if (min != null && max != null) {
                window.dispatchEvent(new CustomEvent("telemetry-zoom", {
                  detail: { min, max, source: channel },
                }));
              }
            }
          }
        ],
      },
      // Channel-specific Y-axis scale ranges
      scales: {
        y: {
          ...(channel === "Throttle" ? { range: [0, 100] as uPlot.Range.MinMax } : {}),
          ...(channel === "Brake" ? { range: [0, 100] as uPlot.Range.MinMax } : {}),
          ...(channel === "SteeringWheelAngle" ? { range: [-75, 75] as uPlot.Range.MinMax } : {}),
          ...(channel === "Speed" ? {
            range: (_u: uPlot, dMin: number, dMax: number): uPlot.Range.MinMax => {
              const pad = (dMax - dMin) * 0.15 || 10;
              return [Math.max(0, Math.floor((dMin - pad) / 10) * 10), Math.ceil((dMax + pad) / 10) * 10];
            },
          } : {}),
        },
      },
      series: [
        {
          label: "Track Position",
          show: false,
          value: (_u, v) => {
            if (v == null) return "-";
            // OT-113: Show corner/straight name in legend tooltip
            const corners = trackModel?.corners || trackModel?.sectors;
            if (corners) {
              const corner = corners.find((c: any) =>
                v >= (c.entry_pct ?? c.start_pct ?? 0) && v <= (c.exit_pct ?? c.end_pct ?? 0)
              );
              if (corner) return `${corner.name} (${(v * 100).toFixed(1)}%)`;
              return (v * 100).toFixed(1) + "%";
            }
            return (v * 100).toFixed(1) + "%";
          },
        },
        ...lapLabels.map((label, i) => ({
          label,
          stroke: customColors?.[i] || getLapColor(i),
          width: 2,
          points: {
            show: false,
            size: 6,
          },
          value: (_u: any, v: any) => {
            if (v == null) return "-";
            if (channel === "SteeringWheelAngle") {
              const abs = Math.abs(v).toFixed(1);
              return v >= 0 ? `Left ${abs}°` : `Right ${abs}°`;
            }
            return v.toFixed(conversion && (conversion.unit === "°" || channel === "Speed") ? 1 : 0) + (conversion ? ` ${conversion.unit}` : "");
          },
        }))
      ],
      axes: [
        {
          stroke: "#94a3b8",
          grid: { stroke: "rgba(148, 163, 184, 0.1)" },
          font: "10px Outfit, sans-serif",
          values: (_u, vals) => {
            // OT-113/OT-214: Zone-based x-axis labels from track model
            // Each tick gets labeled by the zone it falls in; each zone label shown only once
            const corners = trackModel?.corners || trackModel?.sectors;
            if (corners && corners.length >= 2) {
              const sorted = [...corners].sort((a: any, b: any) =>
                (a.entry_pct ?? a.start_pct ?? 0) - (b.entry_pct ?? b.start_pct ?? 0)
              );

              // Build ordered zones: [straight, corner, straight, corner, ...]
              type Zone = { start: number; end: number; name: string };
              const zones: Zone[] = [];

              // Start/finish straight before first corner
              const firstEntry = sorted[0].entry_pct ?? sorted[0].start_pct ?? 0;
              if (firstEntry > 0.01) {
                zones.push({ start: 0, end: firstEntry, name: "Front Stretch" });
              }

              for (let i = 0; i < sorted.length; i++) {
                const c = sorted[i];
                const entry = c.entry_pct ?? c.start_pct ?? 0;
                const exit = c.exit_pct ?? c.end_pct ?? 0;
                // Corner zone
                zones.push({ start: entry, end: exit, name: c.name });
                // Straight between this corner and next
                const nextEntry = i < sorted.length - 1
                  ? (sorted[i + 1].entry_pct ?? sorted[i + 1].start_pct ?? 0)
                  : 1.0;
                if (nextEntry - exit > 0.02) {
                  // Name the straight based on position
                  const midPct = (exit + nextEntry) / 2;
                  const straightName = midPct > 0.85 || midPct < 0.15 ? "Front Stretch" : "Back Stretch";
                  zones.push({ start: exit, end: nextEntry, name: straightName });
                }
              }

              // Map each tick to its zone, only show label once per consecutive zone
              let lastLabel = "";
              return vals.map(v => {
                const zone = zones.find(z => v >= z.start && v < z.end);
                const label = zone ? zone.name : "";
                if (label && label !== lastLabel) {
                  lastLabel = label;
                  return label;
                }
                lastLabel = label;
                return "";
              });
            }
            return vals.map(v => (v * 100).toFixed(0) + "%");
          },
        },
        {
          stroke: "#94a3b8",
          grid: { stroke: "rgba(148, 163, 184, 0.1)" },
          font: "10px Outfit, sans-serif",
          // Steering: show Left/Right labels instead of +/-
          ...(channel === "SteeringWheelAngle" ? {
            values: (_u: any, vals: number[]) => vals.map(v => {
              if (v === 0) return "0°";
              const abs = Math.abs(v).toFixed(0);
              return v > 0 ? `Left ${abs}°` : `Right ${abs}°`;
            }),
          } : {}),
        }
      ],
      cursor: {
        x: true,
        y: false,
        // OT-315: Motec-style drag-to-zoom on x-axis. Drag selects range, all synced charts zoom together.
        drag: { x: true, y: false, setScale: true },
        lock: false,
        focus: { prox: -1 },
        sync: {
          key: "shared-cursor",
        },
      },
      select: {
        show: true,
        over: true,
        left: 0,
        top: 0,
        width: 0,
        height: 0,
      },
      legend: {
        show: true,
        live: true,
      }
    };

    // 3. Create Chart
    uPlotRef.current = new uPlot(opts, data as uPlot.AlignedData, chartRef.current);

    // Handle resize
    const handleResize = () => {
      if (uPlotRef.current && chartRef.current) {
        uPlotRef.current.setSize({
          width: chartRef.current.clientWidth,
          height: height
        });
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (uPlotRef.current) {
        uPlotRef.current.destroy();
        uPlotRef.current = null;
      }
    };
  }, [lapsData, lapLabels, channel, height, units, trackModel]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as TelemetryHoverDetail | number | null;
      if (detail == null || typeof detail === "number") return;
      if (detail.idx != null || typeof detail.lapDistPct !== "number") return;

      const u = uPlotRef.current;
      if (!u) return;

      const xData = u.data?.[0] as number[] | undefined;
      if (!xData || xData.length === 0) return;

      const targetPct = Math.max(xData[0], Math.min(detail.lapDistPct, xData[xData.length - 1]));
      const left = u.valToPos(targetPct, "x");
      const top = typeof u.cursor.top === "number" ? u.cursor.top : 0;

      suppressHoverRef.current = true;
      u.setCursor({ left, top }, false);
      suppressHoverRef.current = false;
    };

    window.addEventListener("telemetry-hover", handler);
    return () => window.removeEventListener("telemetry-hover", handler);
  }, []);

  // OT-315: Listen for zoom events from other graphs and apply to this one
  useEffect(() => {
    const handler = (e: Event) => {
      const { min, max, source } = (e as CustomEvent).detail;
      if (source === channel) return; // Don't re-apply to the graph that triggered it
      const u = uPlotRef.current;
      if (!u) return;
      suppressZoomRef.current = true;
      u.setScale("x", { min, max });
      suppressZoomRef.current = false;
    };
    window.addEventListener("telemetry-zoom", handler);
    return () => window.removeEventListener("telemetry-zoom", handler);
  }, [channel]);

  // OT-315: Apply external zoom range (e.g. from Reset Zoom restoring full range)
  useEffect(() => {
    const u = uPlotRef.current;
    if (!u) return;
    if (zoomRange) {
      suppressZoomRef.current = true;
      u.setScale("x", { min: zoomRange[0], max: zoomRange[1] });
      suppressZoomRef.current = false;
    } else {
      // Reset to full range
      const x = u.data[0];
      if (x && x.length > 0) {
        suppressZoomRef.current = true;
        u.setScale("x", { min: x[0], max: x[x.length - 1] });
        suppressZoomRef.current = false;
      }
    }
  }, [zoomRange]);

  return (
    <div className="telemetry-graph-container" style={{ width: "100%", ...(embedded ? { padding: "12px" } : { background: "var(--card)", borderRadius: "var(--radius-sm)", padding: "12px", border: "1px solid var(--border)" }) }}>
      <style>{`
        /* Fix the legend 'not straight' lines and hide the 'Time' header if it appears */
        .osc-chart .u-legend .u-series:first-child {
          display: none !important;
        }
        .osc-chart .u-legend th:first-child {
          display: none !important;
        }
        .osc-chart .u-marker {
          border-bottom: 2px solid currentColor !important;
          height: 0 !important;
          width: 16px !important;
          margin-top: 8px !important;
          vertical-align: middle !important;
          border-radius: 0 !important;
          background: transparent !important;
        }
        /* Make legend values stand out */
        .osc-chart .u-legend .u-value {
          font-weight: 700;
          color: #fff;
          margin-left: 8px;
        }
        .osc-chart .u-cursor-x {
          border-left: 2px dashed rgba(59, 130, 246, 0.5) !important;
          width: 0 !important;
        }
        .osc-chart .u-select {
          background: rgba(59, 130, 246, 0.14) !important;
          border: 1px solid rgba(59, 130, 246, 0.45);
        }
      `}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <span style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", color: "var(--text2)" }}>
          {conversion ? conversion.label : channel}
        </span>
      </div>
      <div ref={chartRef} />
    </div>
  );
}

function getLapColor(index: number): string {
  const colors = [
    "#3b82f6", // Blue
    "#ef4444", // Red
    "#10b981", // Green
    "#06b6d4", // Cyan
    "#8b5cf6", // Purple
    "#ec4899", // Pink
  ];
  return colors[index % colors.length];
}
